// Conversation list — shared by teacher, parent (observer), and student/kid
// messaging surfaces. Tapping a row selects it (parent controls the selection).
// docs/replan-v2.md R10.

import { useT } from '@fajrarabi/i18n/react';
import { Badge, Skeleton } from '@fajrarabi/ui';

import type { ConversationSummary } from '~/lib/messaging';

export function ConversationList({
  conversations,
  loading,
  selectedId,
  onSelect,
}: {
  conversations: ConversationSummary[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const t = useT();
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (conversations.length === 0) {
    return <p className="px-2 py-6 text-sm text-ink-500">{t('app.messages.empty')}</p>;
  }
  return (
    <ul className="space-y-1">
      {conversations.map((c) => {
        const active = c.id === selectedId;
        return (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onSelect(c.id)}
              className={`flex w-full items-start gap-3 rounded-2xl px-3 py-3 text-start transition-colors ${
                active ? 'bg-burgundy-500/10' : 'hover:bg-cream-200/60'
              }`}
            >
              <span
                aria-hidden="true"
                className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sun-300/60 text-base"
              >
                {c.kind === 'class_broadcast' ? '📢' : '💬'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate font-semibold text-ink-900">{c.title}</span>
                  {c.observer && (
                    <Badge tone="neutral" className="shrink-0">
                      {t('app.messages.observer_badge')}
                    </Badge>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-sm text-ink-500">
                  {c.lastMessagePreview ?? t('app.messages.no_messages_yet')}
                </span>
              </span>
              {c.unreadCount > 0 && (
                <span className="mt-1 grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-burgundy-500 px-1.5 text-xs font-bold text-cream-50">
                  {c.unreadCount}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
